import { useEffect } from 'react';
import { useWebSocket } from '../contexts/WebSocketContext';

/**
 * Questions-specific WebSocket hook.
 * Wraps unified WebSocket with questions page update handling.
 *
 * @param {Function} onQuestionUpdated - Callback when a question is updated (questionId, questionData)
 * @param {Function} onQuestionAnswered - Callback when a question is answered (questionId, answerData)
 * @returns {Object} Questions WebSocket state
 */
export function useQuestionsWebSocket({ onQuestionUpdated, onQuestionAnswered }) {
  const ws = useWebSocket();

  // Subscribe to questions channel
  useEffect(() => {
    if (!ws.isConnected) return;

    ws.subscribe('questions');

    return () => {
      ws.unsubscribe('questions');
    };
    // ws.subscribe/unsubscribe use refs internally and are safe to omit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws.isConnected]);

  // Register message handlers
  useEffect(() => {
    const handleQuestionUpdated = (data) => {
      if (onQuestionUpdated && data.question_id && data.question) {
        onQuestionUpdated(data.question_id, data.question);
      }
    };

    const handleQuestionAnswered = (data) => {
      if (onQuestionAnswered && data.question_id && data.answer) {
        onQuestionAnswered(data.question_id, data.answer);
      }
    };

    // Register handlers
    const cleanupFns = [
      ws.on('question_updated', handleQuestionUpdated),
      ws.on('question_answered', handleQuestionAnswered),
    ];

    return () => {
      // Cleanup all handlers
      cleanupFns.forEach(cleanup => cleanup());
    };
    // ws.on uses refs internally and is safe to omit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onQuestionUpdated, onQuestionAnswered]);

  return {
    isConnected: ws.isConnected,
    error: ws.error,
  };
}
